// styles.js
// Modern, professional styles for Market components

import { COLORS, LAYOUT, TYPOGRAPHY, LOADING_STYLES, CARD_STYLES } from '../../constants';

export const styles = {
  // Main container with modern background
  container: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    position: 'relative',
  },

  // Background decoration for visual appeal
  backgroundDecoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 10% 20%, ${COLORS.primary}05 0%, transparent 50%),
      radial-gradient(circle at 90% 80%, ${COLORS.secondary}03 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },

  // Content wrapper with proper spacing
  content: {
    maxWidth: LAYOUT.maxWidth,
    margin: '0 auto',
    padding: `${LAYOUT.spacing.xl} ${LAYOUT.containerPadding}`,
    position: 'relative',
    zIndex: 1,
  },

  // Modern header section
  header: {
    textAlign: 'center',
    marginBottom: LAYOUT.spacing.xxxl,
    position: 'relative',
  },

  headerCard: {
    ...CARD_STYLES.elevated,
    padding: '48px 32px',
    marginBottom: LAYOUT.spacing.xl,
    background: `linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.grayLight} 100%)`,
    border: `1px solid ${COLORS.primary}10`,
    borderRadius: '24px',
  },

  brandSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },

  brandIcon: {
    width: '56px',
    height: '56px',
    backgroundColor: COLORS.primary,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '20px',
    boxShadow: `0 8px 24px ${COLORS.shadow}`,
  },

  brandText: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.primary,
    fontWeight: '700',
    fontSize: '32px',
    margin: 0,
  },

  title: {
    ...TYPOGRAPHY.heading1,
    color: COLORS.text.primary,
    fontWeight: '700',
    fontSize: '40px',
    marginBottom: '16px',
    letterSpacing: '-0.02em',
  },

  subtitle: {
    ...TYPOGRAPHY.body,
    fontSize: '18px',
    color: COLORS.text.secondary,
    lineHeight: 1.6,
    maxWidth: '600px',
    margin: '0 auto',
  },

  // Stats section for engagement
  statsSection: {
    display: 'flex',
    justifyContent: 'center',
    gap: '48px',
    marginTop: '32px',
    flexWrap: 'wrap',
  },

  statItem: {
    textAlign: 'center',
  },

  statNumber: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.primary,
    fontWeight: '700',
    fontSize: '28px',
    marginBottom: '4px',
  },

  statLabel: {
    ...TYPOGRAPHY.caption,
    color: COLORS.text.secondary,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Filters section with modern card design
  filtersSection: {
    marginBottom: LAYOUT.spacing.xl,
  },

  filtersCard: {
    ...CARD_STYLES.base,
    padding: '24px',
    background: COLORS.white,
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${COLORS.primary}08`,
  },

  // Enhanced listings grid
  listingsSection: {
    marginTop: LAYOUT.spacing.xl,
  },

  listingsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: LAYOUT.spacing.lg,
    flexWrap: 'wrap',
    gap: '16px',
  },

  listingsTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    fontWeight: '600',
    margin: 0,
  },

  listingsCount: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    fontWeight: '500',
  },

  listingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '24px',
    marginTop: LAYOUT.spacing.lg,
  },

  // Modern empty state design
  emptyState: {
    textAlign: 'center',
    padding: '80px 32px',
    background: COLORS.white,
    borderRadius: '20px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${COLORS.primary}08`,
    maxWidth: '600px',
    margin: '0 auto',
  },

  emptyIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: `${COLORS.primary}10`,
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },

  emptyTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    fontWeight: '600',
    marginBottom: '12px',
  },

  emptyText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    lineHeight: 1.6,
    maxWidth: '400px',
    margin: '0 auto 32px',
  },

  emptyAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    borderRadius: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },

  // Enhanced loading state
  loadingContainer: {
    ...LOADING_STYLES.container,
    background: COLORS.white,
    borderRadius: '20px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${COLORS.primary}08`,
    maxWidth: '500px',
    margin: '80px auto',
    padding: '80px 32px',
  },

  loadingSpinner: {
    ...LOADING_STYLES.spinner,
    width: '48px',
    height: '48px',
    borderWidth: '4px',
  },

  loadingText: {
    ...LOADING_STYLES.text,
    fontSize: '18px',
    fontWeight: '500',
    color: COLORS.text.primary,
  },

  // Responsive design
  '@media (max-width: 768px)': {
    headerCard: {
      padding: '32px 24px',
      borderRadius: '20px',
    },
    
    title: {
      fontSize: '32px',
    },
    
    brandText: {
      fontSize: '28px',
    },

    statsSection: {
      gap: '32px',
    },

    listingsGrid: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '20px',
    },
  },
};